import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useDispatch, useSelector} from "react-redux";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import CardActions from "@material-ui/core/CardActions";
import {connect, transceive} from "./actions";
import {useSnackbar} from "notistack";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import SyncAltIcon from '@material-ui/icons/SyncAlt';
import SendIcon from "@material-ui/icons/Send";
import {byteToHexString} from "./util";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  grid: {
    margin: theme.spacing(2), // 移动端: 16px
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(3), // 平板: 24px
    },
    [theme.breakpoints.up('md')]: {
      marginLeft: '10%',
      marginRight: '10%',
    },
  }
}));

function verifyApdu(capdu) {
  let apduArray = [];
  let half = false;
  let halfByte = 0;
  for (let i = 0; i < capdu.length; i += 1) {
    // skip spaces
    if (capdu.substr(i, 1) === ' ') {
      continue;
    }
    let num = parseInt(capdu.substr(i, 1), 16);
    if (0 <= num && num < 16) {
      if (half) {
        apduArray.push((halfByte * 16) + num);
        half = false;
      } else {
        halfByte = num;
        half = true
      }
    } else {
      return {
        error: true,
        errorMsg: `invalid hex: ${capdu.substr(i, 1)}`
      }
    }
  }
  if (half) {
    return {
      error: true,
      errorMsg: 'invalid hex string'
    }
  }

  if (apduArray.length < 4) {
    return {
      error: true,
      errorMsg: 'apdu should have at least four bytes long'
    }
  }

  let cla = apduArray[0];
  let ins = apduArray[1];
  let p1 = apduArray[2];
  let p2 = apduArray[3];
  let nc = 0;
  let ne = 0;
  let data = null;

  if (apduArray.length === 4) {
    // no lc or le
  } else if (apduArray.length === 5) {
    // case 2s
    // only one byte le
    ne = apduArray[4];
    if (ne === 0) {
      ne = 255;
    }
  } else if (apduArray[4] > 0 && apduArray.length === 5 + apduArray[4]) {
    // case 3s
    // only one byte lc
    nc = apduArray[4];
    data = apduArray.slice(5);
    ne = 0;
  } else if (apduArray[4] > 0 && apduArray.length === 6 + apduArray[4]) {
    // case 4s
    // one byte lc + one byte le
    nc = apduArray[4];
    data = apduArray.slice(5, 5 + nc);
    ne = apduArray[5 + nc];
    if (ne === 0) {
      ne = 255;
    }
  } else if (apduArray.length === 7) {
    // case 2e
    // only three bytes of le, first of which is zero
    if (apduArray[4] !== 0) {
      return {
        error: true,
        errorMsg: 'the first byte of three-byte le field cannot be non-zero'
      };
    }
    ne = apduArray[5] * 256 + apduArray[6];
  } else {
    // case 3e & r3
    // three bytes of lc
    if (apduArray[4] !== 0) {
      return {
        error: true,
        errorMsg: 'the first byte of three-byte lc field cannot be non-zero'
      };
    }
    nc = apduArray[5] * 256 + apduArray[6];
    if (nc === 0) {
      return {
        error: true,
        errorMsg: 'three-byte lc field cannot be zero'
      };
    }

    if (apduArray.length === 7 + nc) {
      // case 3e
      // only three bytes of lc
      data = apduArray.slice(7);
      ne = 0;
    } else if (apduArray.length === 9 + nc) {
      // case 4e
      // three bytes of lc and two bytes of le
      data = apduArray.slice(7, 7 + nc);
      ne = apduArray[7 + nc] * 256 + apduArray[8 + nc];
      if (ne === 0) {
        ne = 0x10000;
      }
    } else {
      return {
        error: true,
        errorMsg: 'not in any possible case of apdu'
      };
    }
  }

  return {
    parsedApdu: {
      cla,
      ins,
      p1,
      p2,
      nc,
      ne,
      data
    },
  }

}

// 🔒 安全增强：危险 APDU 命令列表
const DANGEROUS_COMMANDS = [
  {prefix: '00DA', description: 'PUT DATA - 可能覆盖设备数据'},
  {prefix: '00DB', description: 'PUT DATA - 可能覆盖设备数据'},
  {prefix: '00E6', description: 'TERMINATE DF - 可能终止应用'},
  {prefix: '00E4', description: 'DELETE FILE - 可能删除文件'},
  {prefix: '00D4', description: 'CREATE FILE - 可能创建文件'},
  {prefix: '0024', description: 'CHANGE PIN - 修改 PIN'},
  {prefix: '0020', description: 'VERIFY PIN - 验证 PIN'},
  {prefix: '002C', description: 'RESET RETRY COUNTER - 重置重试计数'},
];

/**
 * 🔒 检查 APDU 命令是否危险
 * @param {string} capdu - 十六进制 APDU 命令
 * @returns {Object|null} 危险命令信息或 null
 */
function checkDangerousCommand(capdu) {
  const cmd = capdu.substring(0, 4).toUpperCase();
  return DANGEROUS_COMMANDS.find(dc => cmd === dc.prefix) || null;
}

/* eslint-disable no-throw-literal */
export default function Apdu() {
  const classes = useStyles();
  const device = useSelector(state => state.device);
  const dispatch = useDispatch();
  const [apdu, setApdu] = useState('');
  const [resp, setResp] = useState('');
  const {enqueueSnackbar} = useSnackbar();
  const apduHistory = useSelector(state => state.apduLog);
  
  // 🔒 安全增强：危险命令确认对话框
  const [dangerDialogOpen, setDangerDialogOpen] = useState(false);
  const [dangerInfo, setDangerInfo] = useState(null);

  let {parsedApdu, error, errorMsg} = verifyApdu(apdu);

  const sendApdu = useCallback(async (skipWarning = false) => {
    try {
      if (device === null) {
        if (!await dispatch(connect())) {
          throw 'Cannot connect to GriffonOakey';
        }
      }

      let capdu = apdu.replace(/\s/g, '');
      
      // 🔒 验证长度（防止超长命令）
      if (capdu.length > 1024) {
        throw new Error('APDU 命令太长（最大 512 字节）');
      }
      
      // 🔒 检查是否为危险命令
      if (!skipWarning) {
        const danger = checkDangerousCommand(capdu);
        if (danger) {
          setDangerInfo(danger);
          setDangerDialogOpen(true);
          return; // 等待用户确认
        }
      }
      
      setResp(await dispatch(transceive(capdu)));
      enqueueSnackbar('Command APDU sent', {variant: 'success'});
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
  }, [device, apdu, dispatch, enqueueSnackbar]);
  
  // 🔒 用户确认危险命令
  const handleDangerConfirm = useCallback(() => {
    setDangerDialogOpen(false);
    sendApdu(true); // 跳过警告，直接发送
  }, [sendApdu]);
  
  const handleDangerCancel = useCallback(() => {
    setDangerDialogOpen(false);
    setDangerInfo(null);
  }, []);

  return (
    <div className={classes.root}>
      <Grid container spacing={1} justifyContent={"center"}>
        <Grid item xs={5}>
          <Card>
            <CardContent>
              <Typography variant="h4" gutterBottom>
                Send Command APDU
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Send raw APDU commands to the device. Enter APDU in hexadecimal format (e.g., 00A4040006D27600012401).
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                ⚠️ Warning: Sending incorrect commands may damage device data. Use with caution.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                placeholder="Input APDU here (hex format)"
                error={error}
                helperText={errorMsg}
                onChange={(e) => setApdu(e.target.value)}/>
              {
                parsedApdu ?
                  <div>
                    <Typography>
                      CLA: {byteToHexString([parsedApdu.cla])}
                    </Typography>
                    <Typography>
                      INS: {byteToHexString([parsedApdu.ins])}
                    </Typography>
                    <Typography>
                      P1: {byteToHexString([parsedApdu.p1])}
                    </Typography>
                    <Typography>
                      P2: {byteToHexString([parsedApdu.p2])}
                    </Typography>
                    {
                      parsedApdu.data ?
                        <Typography>
                          Command Data: {byteToHexString(parsedApdu.data)}
                        </Typography> : null
                    }
                    <Typography>
                      N<sub>e</sub> (Maximum response bytes): {parsedApdu.ne.toString(16)}
                    </Typography>
                  </div> : null
              }
              {
                resp ?
                  <Typography>
                    Resp: {resp}
                  </Typography> : null
              }
            </CardContent>
            <CardActions>
              <Tooltip title="Send APDU">
                <IconButton onClick={sendApdu}>
                  <SendIcon/>
                </IconButton>
              </Tooltip>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={5}>
          <Card>
            <CardContent>
              <Typography variant="h4" gutterBottom>
                APDU History
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                History of APDU commands sent during this session (from earliest to latest).
              </Typography>
              <List>
                {
                  apduHistory.map((entry, index) => {
                    return <ListItem key={`${entry.capdu}-${entry.rapdu}-${index}`}>
                      <ListItemAvatar>
                        <SyncAltIcon/>
                      </ListItemAvatar>
                      <ListItemText primary={`Command: ${entry.capdu}`} secondary={`Response: ${entry.rapdu}`}/>
                    </ListItem>;
                  })
                }
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* 🔒 危险命令确认对话框 */}
      <Dialog
        open={dangerDialogOpen}
        onClose={handleDangerCancel}
        aria-labelledby="danger-dialog-title"
      >
        <DialogTitle id="danger-dialog-title">
          ⚠️ 危险 APDU 命令警告
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            您正在尝试发送一个可能造成损害的 APDU 命令：
            <br/>
            <br/>
            <strong>{dangerInfo?.description}</strong>
            <br/>
            <br/>
            命令: <code>{apdu.substring(0, 4).toUpperCase()}</code>
            <br/>
            <br/>
            此命令可能：
            <ul>
              <li>覆盖或删除设备中的数据</li>
              <li>修改设备配置</li>
              <li>导致设备无法使用</li>
            </ul>
            <strong>您确定要继续吗？</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDangerCancel} color="primary" autoFocus>
            取消
          </Button>
          <Button onClick={handleDangerConfirm} color="secondary">
            我知道风险，继续发送
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
